import {
  createContext,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDefinedContext } from '@shared/hooks/useDefinedContext';
import { flushAllGuestPricingImpressionQueues } from './guestPricingImpressionQueue';

export interface GuestPricingBrowseContextValue {
  serviceWindowIndex: number;
  setServiceWindowIndex: (index: number) => void;
  resetServiceWindowIndex: () => void;
}

export const GuestPricingBrowseContext =
  createContext<GuestPricingBrowseContextValue | null>(null);
GuestPricingBrowseContext.displayName = 'GuestPricingBrowseContext';

export const GuestPricingBrowseContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [serviceWindowIndex, setServiceWindowIndex] = useState(0);
  const resetServiceWindowIndex = useCallback(() => {
    setServiceWindowIndex(0);
  }, []);

  useEffect(() => {
    const flushUnloadSafe = () => {
      flushAllGuestPricingImpressionQueues({ unloadSafe: true });
    };
    window.addEventListener('pagehide', flushUnloadSafe);
    return () => {
      flushUnloadSafe();
      window.removeEventListener('pagehide', flushUnloadSafe);
    };
  }, []);

  const value = useMemo<GuestPricingBrowseContextValue>(
    () => ({
      serviceWindowIndex,
      setServiceWindowIndex,
      resetServiceWindowIndex,
    }),
    [serviceWindowIndex, resetServiceWindowIndex],
  );

  return (
    <GuestPricingBrowseContext.Provider value={value}>
      {children}
    </GuestPricingBrowseContext.Provider>
  );
};

export const useGuestPricingBrowseContext = () =>
  useDefinedContext(GuestPricingBrowseContext);
